import { memo, ReactNode, useEffect, useRef, useState } from 'react';

import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import { DAAS_APPLICATION_TYPE } from '../../../../types';
import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import { DAAS_NAME_MAX_LENGTH } from '../../nameLimits';
import {
  APPLICATION_DESCRIPTION_INFO,
  APPLICATION_NAME_INFO,
} from '../infoDoc';
import { useStyles } from '../styles';
import { ApplicationBasicInfoValue } from '../types';
import { applicationNameFormatErrors } from '../validation';

const DESCRIPTION_MAX_LENGTH = 250;

export interface ApplicationBasicInfoSectionProps {
  value: ApplicationBasicInfoValue;
  selectedProject: string;
  onChange: (value: ApplicationBasicInfoValue) => void;
  errors: string[];
  readOnly?: boolean;
}

const ApplicationBasicInfoSectionInner = ({
  value,
  selectedProject,
  onChange,
  errors,
  readOnly = false,
}: ApplicationBasicInfoSectionProps) => {
  const { classes } = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [nameWarning, setNameWarning] = useState<string | undefined>();
  const [existingNames, setExistingNames] = useState<Set<string>>(new Set());
  const nameRef = useRef<HTMLDivElement>(null);

  const nameError =
    errors.length > 0 ? applicationNameFormatErrors(value.applicationName) : '';
  const hasError = nameError !== '';

  useEffect(() => {
    if (hasError || nameWarning) {
      nameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hasError, nameWarning]);

  useEffect(() => {
    let cancelled = false;

    if (readOnly || !selectedProject) {
      setExistingNames(new Set());
    } else {
      catalogApi
        .getEntities({
          filter: {
            kind: 'Component',
            'spec.type': DAAS_APPLICATION_TYPE,
            'spec.projectId': selectedProject,
          },
          fields: ['spec.applicationName'],
        })
        .then(result => {
          if (cancelled) return;
          setExistingNames(
            new Set(
              result.items
                .map(e => e.spec?.applicationName as string)
                .filter(Boolean),
            ),
          );
        })
        .catch(() => {
          if (!cancelled) setExistingNames(new Set());
        });
    }

    return () => {
      cancelled = true;
    };
  }, [selectedProject, catalogApi, readOnly]);

  useEffect(() => {
    if (!value.applicationName || !value.applicationName.trim()) {
      setNameWarning(undefined);
      return;
    }
    setNameWarning(
      existingNames.has(value.applicationName)
        ? `Application "${value.applicationName}" already exists in project "${selectedProject}"`
        : undefined,
    );
  }, [value.applicationName, existingNames, selectedProject]);

  let appNameHelperText: ReactNode;
  if (readOnly) {
    appNameHelperText = 'Application name cannot be changed after creation.';
  } else if (nameWarning) {
    appNameHelperText = <ScaffolderFieldError message={nameWarning} />;
  } else if (hasError) {
    appNameHelperText = <ScaffolderFieldError message={nameError} />;
  } else {
    appNameHelperText =
      'Up to 46 characters. Allowed: lowercase letters, numbers, hyphens.';
  }

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Application configuration"
          description="Configure a new Deployment as a Service Application."
          variant="h5"
          divider
        />
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Application Name"
            description="Specify a unique name for the application."
            infoDoc={APPLICATION_NAME_INFO}
          />
          <TextField
            ref={nameRef}
            label="Application Name"
            value={value.applicationName}
            onChange={e =>
              readOnly
                ? undefined
                : onChange({ ...value, applicationName: e.target.value })
            }
            variant="outlined"
            size="small"
            fullWidth
            required
            disabled={readOnly}
            error={!readOnly && (hasError || Boolean(nameWarning))}
            slotProps={{
              htmlInput: { maxLength: DAAS_NAME_MAX_LENGTH },
            }}
            helperText={appNameHelperText}
          />
        </Box>
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Description"
            description="Specify a description of the application."
            infoDoc={APPLICATION_DESCRIPTION_INFO}
          />
          <TextField
            label="Description"
            value={value.description}
            onChange={e => onChange({ ...value, description: e.target.value })}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={3}
            slotProps={{
              htmlInput: { maxLength: DESCRIPTION_MAX_LENGTH },
            }}
            helperText="Optional. Up to 250 characters."
          />
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const ApplicationBasicInfoSection = memo(
  ApplicationBasicInfoSectionInner,
);
